import { OP_ED_SKIP_MINUTES } from '../config/constants';

/**
 * Format duration in minutes to human-readable format
 * Shows years, months, days, hours, and minutes, hiding zero values
 * @param {number} minutes - Duration in minutes
 * @param {number} hoursPerDay - Hours per day (default 24)
 * @returns {string} Formatted duration (e.g., "2y 3M 5d 2h 30m", "5d 2h 30m", "45m")
 */
export const formatDuration = (minutes, hoursPerDay = 24) => {
    if (!minutes || minutes <= 0) return '0m';

    const minutesPerDay = hoursPerDay * 60;
    const minutesPerMonth = minutesPerDay * 30; // Approximate: 30 days per month
    const minutesPerYear = minutesPerDay * 365; // Approximate: 365 days per year

    const years = Math.floor(minutes / minutesPerYear);
    const months = Math.floor((minutes % minutesPerYear) / minutesPerMonth);
    const days = Math.floor((minutes % minutesPerMonth) / minutesPerDay);
    const hours = Math.floor((minutes % minutesPerDay) / 60);
    const mins = Math.floor(minutes % 60);

    const parts = [];

    if (years > 0) parts.push(`${years}y`);
    if (months > 0) parts.push(`${months}M`);
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);

    return parts.length > 0 ? parts.join(' ') : '0m';
};

/**
 * Get the effective total episode count for an anime
 * For ongoing series, prefers last aired episode (nextAiringEpisode - 1)
 * @param {Object} entry - Anime list entry
 * @returns {number} Total episode count
 */
export const getTotalEpisodes = (entry) => {
    const mediaStatus = entry.media?.status;

    // NOT_YET_RELEASED: nothing has aired yet → 0 episodes
    if (mediaStatus === 'NOT_YET_RELEASED') {
        return 0;
    }

    // For ongoing series, prioritize last aired episode from nextAiringEpisode
    // This ensures we only count UP TO the released episodes, not the total planned
    if (entry.media?.nextAiringEpisode?.episode) {
        return entry.media.nextAiringEpisode.episode - 1;
    }

    // If total episodes is defined (and no next episode info), use it
    if (entry.media?.episodes) {
        return entry.media.episodes;
    }

    // Fallback: get maximum episode number from airingSchedule
    const airedEpisodes = entry.media?.airingSchedule?.nodes || [];
    if (airedEpisodes.length > 0) {
        const maxAiredEpisode = Math.max(...airedEpisodes.map(ep => ep.episode));
        return maxAiredEpisode;
    }

    // Final fallback to user's progress
    return entry.progress || 0;
};

/**
 * Calculate total, watched, and remaining time for a single anime entry
 * @param {Object} entry - Anime list entry
 * @returns {Object} Time statistics {total, watched, remaining} in minutes
 */
export const calculateAnimeTime = (entry) => {
    const totalEpisodes = getTotalEpisodes(entry);
    const watchedEpisodes = entry.progress || 0;
    const duration = entry.media?.duration || 24; // Default 24 min per episode
    const adjustedDuration = duration >= 10 ? Math.max(0, duration - OP_ED_SKIP_MINUTES) : duration; // Subtract OP/ED skip only for standard-length episodes

    const totalMinutes = totalEpisodes * adjustedDuration;
    const watchedMinutes = watchedEpisodes * adjustedDuration;
    const remainingMinutes = totalMinutes - watchedMinutes;

    return {
        total: totalMinutes,
        watched: watchedMinutes,
        remaining: remainingMinutes
    };
};

/**
 * Get a display-friendly media status badge info
 * Maps AniList media.status to emoji + label + CSS class
 * @param {string} status - Media status from AniList (RELEASING, FINISHED, NOT_YET_RELEASED, CANCELLED, HIATUS)
 * @returns {Object} { emoji, label, className }
 */
export const getMediaStatusBadge = (status) => {
    const badges = {
        'RELEASING': { emoji: '📡', label: 'Airing', className: 'status-airing' },
        'FINISHED': { emoji: '✅', label: 'Finished', className: 'status-finished' },
        'NOT_YET_RELEASED': { emoji: '📅', label: 'Upcoming', className: 'status-upcoming' },
        'CANCELLED': { emoji: '❌', label: 'Cancelled', className: 'status-cancelled' },
        'HIATUS': { emoji: '⏸️', label: 'On Hiatus', className: 'status-hiatus' },
    };
    return badges[status] || { emoji: '❓', label: status || 'Unknown', className: 'status-unknown' };
};

/**
 * Format episode progress as "watched/total"
 * @param {number} watched - Episodes watched
 * @param {number} total - Total episodes (or aired episodes for ongoing series)
 * @returns {string} Formatted progress (e.g., "5/24", "1050/1120" for ongoing)
 */
export const formatEpisodeProgress = (watched, total) => {
    const watchedCount = watched || 0;
    const totalCount = total || 0; // Don't fallback to watchedCount
    return `${watchedCount}/${totalCount}`;
};

/**
 * Calculate aggregated stats for a single Overview tab using group-level data.
 * Sums totalDuration, watchedDuration, episodes, anime count, and average score
 * from all groups belonging to the specified tab.
 * @param {Array} groups - All anime groups (from animeGroups context)
 * @param {string} tabId - Tab filter ID (e.g., 'CURRENT', 'CONTINUE', 'COMPLETED', 'ALL', etc.)
 * @param {Function} filterFn - filterGroupsByStatus function
 * @returns {object} Aggregated stats for the tab
 */
export const calculateTabStats = (groups, tabId, filterFn) => {
    if (!groups || groups.length === 0) {
        return {
            groupCount: 0,
            animeCount: 0,
            totalDuration: 0,
            watchedDuration: 0,
            remainingDuration: 0,
            totalEpisodes: 0,
            watchedEpisodes: 0,
            averageScore: 0
        };
    }

    const filtered = filterFn(groups, tabId === 'ALL' ? null : tabId);

    let totalDuration = 0;
    let watchedDuration = 0;
    let totalEpisodes = 0;
    let watchedEpisodes = 0;
    let totalScore = 0;
    let scoredCount = 0;
    let animeCount = 0;

    filtered.forEach(group => {
        const s = group.stats;
        totalDuration += s.totalDuration || 0;
        watchedDuration += s.watchedDuration || 0;
        totalEpisodes += s.totalEpisodes || 0;
        watchedEpisodes += s.watchedEpisodes || 0;
        animeCount += s.animeCount || 0;
        if (s.averageScore > 0) {
            totalScore += s.averageScore;
            scoredCount++;
        }
    });

    return {
        groupCount: filtered.length,
        animeCount,
        totalDuration,
        watchedDuration,
        remainingDuration: Math.max(0, totalDuration - watchedDuration),
        totalEpisodes,
        watchedEpisodes,
        averageScore: scoredCount > 0 ? Math.round(totalScore / scoredCount) : 0
    };
};

// ==================== MANGA UTILITY FUNCTIONS ====================

/**
 * Get total chapters for a manga entry (handles ongoing/unreleased manga)
 * @param {Object} entry - AniList manga entry
 * @param {Object} [latestChapterMap] - Optional map from MangaUpdates { anilistId: { latestChapter } }
 * @returns {number|null} Total chapters, or null if unknown
 */
export const getTotalChapters = (entry, latestChapterMap = null) => {
    if (entry.media?.status === 'NOT_YET_RELEASED') return 0;
    if (entry.media?.chapters) return entry.media.chapters;
    // Check MangaUpdates data for ongoing manga
    if (latestChapterMap) {
        const muData = latestChapterMap[String(entry.media?.id)];
        if (muData?.latestChapter) return muData.latestChapter;
    }
    // Unknown total — return null to show "?" instead of misleading progress
    return null;
};

/**
 * Get total volumes for a manga entry
 * @param {Object} entry - AniList manga entry
 * @returns {number|null} Total volumes, or null if unknown
 */
export const getTotalVolumes = (entry) => {
    if (entry.media?.status === 'NOT_YET_RELEASED') return 0;
    if (entry.media?.volumes) return entry.media.volumes;
    // Unknown total — return null to show "?"
    return null;
};

/**
 * Calculate chapter/volume progress for a manga entry
 * (Manga equivalent of calculateAnimeTime)
 * @param {Object} entry - AniList manga entry
 * @param {Object} [latestChapterMap] - Optional map from MangaUpdates
 */
export const calculateMangaProgress = (entry, latestChapterMap = null) => {
    const totalChapters = getTotalChapters(entry, latestChapterMap);
    const readChapters = entry.progress || 0;
    const totalVolumes = getTotalVolumes(entry);
    const readVolumes = entry.progressVolumes || 0;

    return {
        totalChapters,
        readChapters,
        remainingChapters: totalChapters !== null ? Math.max(0, totalChapters - readChapters) : null,
        totalVolumes,
        readVolumes,
        remainingVolumes: totalVolumes !== null ? Math.max(0, totalVolumes - readVolumes) : null,
        percentComplete: totalChapters !== null && totalChapters > 0
            ? Math.round((readChapters / totalChapters) * 100)
            : null
    };
};

/**
 * Format chapter progress as a readable string
 */
export const formatChapterProgress = (read, total) => {
    if (total && total > 0) {
        return `${read || 0}/${total}`;
    }
    return `${read || 0}/?`;
};

/**
 * Format volume progress as a readable string
 */
export const formatVolumeProgress = (read, total) => {
    if (total && total > 0) {
        return `${read || 0}/${total}`;
    }
    return `${read || 0}/?`;
};

/**
 * Get manga media status badge (equivalent of getMediaStatusBadge for anime)
 */
export const getMangaMediaStatusBadge = (status) => {
    const badges = {
        'RELEASING': { emoji: '📡', label: 'Publishing', className: 'status-airing' },
        'FINISHED': { emoji: '✅', label: 'Finished', className: 'status-finished' },
        'NOT_YET_RELEASED': { emoji: '📅', label: 'Not Published', className: 'status-upcoming' },
        'CANCELLED': { emoji: '🚫', label: 'Cancelled', className: 'status-cancelled' },
        'HIATUS': { emoji: '⏸️', label: 'Hiatus', className: 'status-hiatus' }
    };
    return badges[status] || { emoji: '❓', label: status || 'Unknown', className: 'status-unknown' };
};

/**
 * Calculate manga tab stats (equivalent of calculateTabStats for anime)
 * Groups manga by tab/status and computes chapter/volume aggregate counts.
 */
export const calculateMangaTabStats = (groups, tabId, filterFn = null) => {
    if (!groups || groups.length === 0) return null;

    const filtered = filterFn ? groups.filter(filterFn) : groups;
    if (filtered.length === 0) return null;

    let mangaCount = 0;
    let totalChapters = 0;
    let readChapters = 0;
    let totalVolumes = 0;
    let readVolumes = 0;
    let totalScore = 0;
    let scoredCount = 0;

    filtered.forEach(group => {
        const entries = group.anime || group.manga || [];
        entries.forEach(entry => {
            mangaCount++;
            const progress = calculateMangaProgress(entry);
            if (progress.totalChapters !== null) totalChapters += progress.totalChapters;
            readChapters += progress.readChapters;
            if (progress.totalVolumes !== null) totalVolumes += progress.totalVolumes;
            readVolumes += progress.readVolumes;

            const score = entry.media?.averageScore || entry.score || 0;
            if (score > 0) {
                totalScore += score;
                scoredCount++;
            }
        });
    });

    return {
        groupCount: filtered.length,
        mangaCount,
        totalChapters,
        readChapters,
        remainingChapters: Math.max(0, totalChapters - readChapters),
        totalVolumes,
        readVolumes,
        remainingVolumes: Math.max(0, totalVolumes - readVolumes),
        averageScore: scoredCount > 0 ? Math.round(totalScore / scoredCount) : 0
    };
};
